/**
 * SpeakingPractice (Pha 3 — Luyện nói + chấm phát âm).
 *
 * PII nhạy cảm (giọng học viên) — tuân quyết định compliance D-1…D-7:
 *   - Chặn consent: chưa đồng ý → hiện `SpeakingConsentForm` (v3), KHÔNG cho ghi âm.
 *   - Ghi âm → gửi chấm (Gemini) → hiện điểm + transcript + nhận xét (server không lưu audio).
 *   - Nút "Xoá dữ liệu luyện nói của tôi".
 *
 * Hiển thị thay cho phần "Luyện nói" tĩnh khi LanguageLessonView có `lessonSlug`.
 */

import { useRef, useState } from "react";
import { Alert, Button, Card, Flex, Popconfirm, Space, Spin, Tag, Typography, message } from "antd";
import { AudioOutlined, DeleteOutlined, SoundOutlined } from "@ant-design/icons";
import type { MessageInstance } from "antd/es/message/interface";
import type { LangSpeaking } from "@/api/catalog";
import {
  fetchSpeakingResult,
  useDeleteSpeakingData,
  useScoreSpeaking,
  useSpeakingConsent,
} from "@/api/learning";
import { ScoreTag, pctTo10, scoreColor } from "@/components/ScoreTag";
import { SpeakingConsentForm } from "@/components/SpeakingConsentForm";

const { Text } = Typography;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface AxiosErr {
  response?: { status?: number };
}

interface Props {
  lessonSlug: string;
  speaking: LangSpeaking[];
}

export function SpeakingPractice({ lessonSlug, speaking }: Props) {
  const { data: consent, isLoading } = useSpeakingConsent();
  const deleteData = useDeleteSpeakingData();
  const [messageApi, holder] = message.useMessage();
  if (speaking.length === 0) return null;

  const handleDelete = async () => {
    try {
      await deleteData.mutateAsync();
      messageApi.success("Đã xoá dữ liệu luyện nói của bạn");
    } catch {
      messageApi.error("Xoá thất bại, thử lại");
    }
  };

  return (
    <Card size="small" title="Luyện nói" style={{ marginTop: 16 }} data-testid="speaking-practice">
      {holder}

      {isLoading ? (
        <Spin />
      ) : !consent?.is_active ? (
        // ─── Consent gate (v3) ──────────────────────────────────────────
        <SpeakingConsentForm />
      ) : (
        // ─── Đã consent: ghi âm từng câu ────────────────────────────────
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {speaking.map((s, i) => (
            <SpeakingItem
              key={i}
              lessonSlug={lessonSlug}
              promptIndex={i}
              item={s}
              messageApi={messageApi}
            />
          ))}
          <Flex justify="flex-end">
            <Popconfirm
              title="Xoá dữ liệu luyện nói?"
              description="Xoá điểm luyện nói trong bài học và tắt tính năng. Bản ghi câu Phát âm trong bài tập/bài kiểm tra là hồ sơ đánh giá, không bị xoá ở đây."
              okText="Xoá"
              cancelText="Huỷ"
              onConfirm={handleDelete}
            >
              <Button size="small" danger icon={<DeleteOutlined />} loading={deleteData.isPending}>
                Xoá dữ liệu luyện nói của tôi
              </Button>
            </Popconfirm>
          </Flex>
        </Space>
      )}
    </Card>
  );
}

function SpeakingItem({
  lessonSlug,
  promptIndex,
  item,
  messageApi,
}: {
  lessonSlug: string;
  promptIndex: number;
  item: LangSpeaking;
  messageApi: MessageInstance;
}) {
  const score = useScoreSpeaking();
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false); // đang chấm/poll
  const [result, setResult] = useState<{
    score: number;
    transcript: string;
    feedback: string;
  } | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof window !== "undefined" &&
    "MediaRecorder" in window;

  // Poll kết quả async (Nấc 2): tối đa ~30s (20 lần × 1.5s).
  const pollResult = async (jobId: string) => {
    for (let i = 0; i < 20; i++) {
      await sleep(1500);
      try {
        const r = await fetchSpeakingResult(jobId);
        if (r.status === "done") {
          setResult({
            score: r.score ?? 0,
            transcript: r.transcript ?? "",
            feedback: r.feedback ?? "",
          });
          return;
        }
        if (r.status === "error") {
          messageApi.error(r.detail ?? "Chấm thất bại, thử lại.");
          return;
        }
      } catch {
        // 404 tạm thời / mạng — thử lại vài lần.
      }
    }
    messageApi.warning("Server đang bận, kết quả lâu hơn dự kiến. Thử lại sau.");
  };

  const submit = async (blob: Blob) => {
    setBusy(true);
    try {
      const data = await score.mutateAsync({
        lessonSlug,
        promptIndex,
        targetJa: item.target_ja ?? "",
        audio: blob,
      });
      if ("job_id" in data) {
        await pollResult(data.job_id);
      } else {
        setResult({
          score: data.score,
          transcript: data.transcript,
          feedback: data.feedback ?? "",
        });
      }
    } catch (err) {
      const st = (err as AxiosErr)?.response?.status;
      if (st === 502) {
        messageApi.error("Chưa chấm được phát âm, thử lại nhé.");
      } else if (st === 503) {
        messageApi.warning("Server đang bận chấm phát âm. Thử lại sau giây lát.");
      } else if (st === 429) {
        messageApi.warning("Bạn luyện hơi nhiều rồi — nghỉ chút rồi thử lại nhé.");
      } else if (st === 403) {
        messageApi.warning("Cần đồng ý điều khoản luyện nói trước.");
      } else {
        messageApi.error("Không chấm được, thử lại.");
      }
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (!supported) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        // Mime thật của trình duyệt (Safari iOS = audio/mp4) — Gemini cần đúng định dạng.
        void submit(new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" }));
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setRecording(false);
      messageApi.error("Không truy cập được micro. Kiểm tra quyền trình duyệt.");
    }
  };

  const stop = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <div data-testid="speaking-item">
      {item.prompt_vi && <Text>{item.prompt_vi}</Text>}
      {item.target_ja && (
        <Flex align="center" gap={8} wrap>
          {item.audio && (
            <Button
              size="small"
              type="text"
              icon={<SoundOutlined />}
              aria-label={`Nghe mẫu: ${item.target_ja}`}
              onClick={() => void new Audio(item.audio).play().catch(() => {})}
            />
          )}
          <Text strong>{item.target_ja}</Text>
          {!supported ? (
            <Tag color="default">Trình duyệt không hỗ trợ ghi âm</Tag>
          ) : recording ? (
            <Button size="small" danger onClick={stop} data-testid="speaking-stop">
              Dừng
            </Button>
          ) : (
            <Button
              size="small"
              icon={<AudioOutlined />}
              loading={busy}
              onClick={start}
              data-testid="speaking-record"
            >
              {busy ? "Đang chấm…" : "Ghi âm"}
            </Button>
          )}
        </Flex>
      )}
      {result &&
        (() => {
          const score10 = pctTo10(result.score);
          const color = scoreColor(score10);
          const alertType = color === "red" ? "error" : color === "gold" ? "warning" : "success";
          return (
            <Alert
              style={{ marginTop: 4 }}
              type={alertType}
              message={
                <>
                  Điểm phát âm: <ScoreTag value={score10} />
                </>
              }
              description={
                <>
                  <div>Bạn đã đọc: {result.transcript}</div>
                  {result.feedback && <div>Nhận xét: {result.feedback}</div>}
                </>
              }
              data-testid="speaking-result"
            />
          );
        })()}
    </div>
  );
}
